import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query"; // 1. Import useQuery
import Pagination from "../commons/Pagination";
import { baseAxios } from "../../apis";
import moment from "moment";
import SkeletonCard from "../skeletons/SkeletonCard";
import { useBot } from "../../context/botContext";

// 2. Create a dedicated hook for fetching the paginated logs.
const useBotLogQuery = (botId, page, limit) => {
  return useQuery({
    // The query key includes all dependencies for automatic refetching.
    queryKey: ["botLog", botId, page, limit],
    queryFn: async () => {
      const { data } = await baseAxios.get(`/api/bot_log`, {
        params: {
          bot_id: botId,
          page: page,
          limit: limit,
        },
      });
      return data;
    },
    // Only run the query if a bot is selected.
    enabled: !!botId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes,
    // Keep the previous page's data visible while the next page loads.
    placeholderData: (previousData) => previousData,
  });
};

export const LogBot = () => {
  const { selectedBot } = useBot();

  // UI-controlled state for pagination remains.
  const [limit, setLimit] = useState(5);
  const [page, setPage] = useState(1);

  // 3. Remove the manual state for data, total pages, and loading.
  // const [totalPages, setTotalPages] = useState(1);
  // const [tradeLogs, setTradeLogs] = useState([]);
  // const [loading, setLoading] = useState(false);

  // 4. Remove the data-fetching useEffect.
  // useEffect(() => { ... }, [page, limit, selectedBot]);

  // 5. Call our new hook to get data and state.
  const { data, isLoading: loading } = useBotLogQuery(
    selectedBot?.id,
    page,
    limit
  );

  // 6. Derive state from the query's result.
  const tradeLogs = data?.logs || [];
  const totalPages = data?.total_pages || 1;

  // The rest of the component JSX remains the same.
  return (
    <div className="rounded-xl">
      <div className="flex justify-between items-center mb-3">
        <div className="relative z-[11] hidden md:flex items-center bg-white text-[#697077] text-sm px-5 pr-3 rounded-lg ml-auto">
          <span className="mr-2">View</span>
          <select
            name="view-table"
            id="view-table"
            className="border-none cursor-pointer outline-none m-0 text-sm py-1.5"
            onChange={(e) => {
              setLimit(e.target.value);
              setPage(1); // Reset to page 1 when limit changes
            }}
            value={limit}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
          </select>
        </div>
      </div>
      <div className="rounded-xl border w-full overflow-x-auto">
        <table className="w-full text-sm text-left text-[#344054]">
          <thead className="text-sm bg-[#F2F4F8] font-medium text-[#667085] border-b border-[#eaecf0]">
            <tr>
              <th className="p-4 rounded-tl-xl">ID.</th>
              <th className="p-4">Coin ID</th>
              <th className="p-4">Status</th>
              <th className="p-4">Message</th>
              {/* <th className="p-4">Created At</th> */}
              <th className="p-4 rounded-tr-xl">Updated At</th>
            </tr>
          </thead>
          <tbody className="text-sm text-[#101828]">
            {loading && !data ? ( // Show skeleton only on the very first load
              [...Array(limit)].map((_, idx) => (
                <tr key={idx} className="border-b border-[#f2f4f7] bg-white">
                  <td
                    className={`p-4 text-sm ${
                      limit - 1 === idx && "rounded-bl-xl"
                    }`}
                  >
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4 text-sm">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className={`p-4 text-sm font-medium`}>
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4 text-sm">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["200px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4 text-sm">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  {/* <td
                    className={`p-4 text-sm ${
                      limit - 1 === idx && "rounded-br-xl"
                    }`}
                  >
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td> */}
                </tr>
              ))
            ) : tradeLogs?.length ? (
              tradeLogs.map((item, idx) => (
                <tr
                  key={item.id}
                  className="border-b border-[#f2f4f7] bg-white"
                >
                  <td
                    className={`p-4 text-sm ${
                      tradeLogs?.length - 1 === idx && "rounded-bl-xl"
                    }`}
                  >
                    {item.id}
                  </td>
                  <td className="p-4 text-sm">{item.coin_name}</td>
                  <td className="p-4 text-sm">{item.status}</td>
                  <td className="p-4 text-sm">{item.message}</td>
                  {/* <td className="p-4 text-sm">
                    {moment(item.created_at).format("DD/MM/YYYY HH:mm")}
                  </td> */}
                  <td
                    className={`p-4 text-sm ${
                      tradeLogs?.length - 1 === idx && "rounded-br-xl"
                    }`}
                  >
                    {moment(item.updated_at).format("DD/MM/YYYY HH:mm")}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="h-[10rem]  p-3 text-sm text-center text-gray-400"
                  colSpan={6}
                >
                  No logs found for this bot.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="items-center justify-center mt-4 md:mt-6 flex">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};
